<script setup>
import CookieBanner from '@/components/CookieBanner.vue'
import LoginModal from '@/components/LoginModal.vue'
import { useMainStore } from '@/stores/main.js'
import { storeToRefs } from 'pinia'
import { router } from '@inertiajs/vue3'

const props = defineProps({
    errors: Object,
    flash: Object,
    cart_data: Object,
})

const store = useMainStore()
const {
    cartCount,
    user,
    userName,
    isUserPage,
    isDashboardActive,
    isProfileActive,
    sidebarOpen,
    isOrdersActive,
} = storeToRefs(store)

const logout = () => {
    router.post(route('process-logout'))
}
</script>
<script>
export default {
  data: () => ({
  }),
}
</script>

<template>
    <v-app>
        <header class="sticky top-0 z-40 bg-white/90 backdrop-blur shadow-sm">
            <div class="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
                <div class="flex items-center gap-3">
                    <!-- Mobile menu button for user pages -->
                    <button
                        v-if="isUserPage && user"
                        @click="store.toggleSidebar()"
                        class="md:hidden p-2 text-iron-grey/70 hover:text-iron-grey transition-colors"
                        aria-label="Toggle menu"
                    >
                        <svg class="h-6 w-6" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                            <path v-if="!sidebarOpen" stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                            <path v-else stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <a :href="route('web.home')" class="flex items-center gap-2">
                        <img src="/images/common/logo.svg" alt="Logo" class="h-16 w-30 p-2" />
                    </a>
                </div>
                
                <div class="md:hidden flex items-center gap-3">
                    <v-menu>
                        <template v-slot:activator="{ props }">
                            <v-btn icon="mdi-menu" variant="text" v-bind="props" size="x-large"></v-btn>
                        </template>

                        <v-list>
                            <v-list-item>
                                <LoginModal :errors="errors" :flash="flash" is-menu/>
                            </v-list-item>
                            <v-list-item>
                                <button
                                    @click="router.visit(route('web.b2b'))"
                                    class="w-full relative inline-flex items-center gap-2  px-6 py-4 text-sm font-semibold text-iron-grey border-2 border-iron-grey hover:bg-iron-grey hover:text-white"
                                >
                                
                                    Za podjetja
                                </button>
                            </v-list-item>
                            <v-list-item>
                                <a
                                    :href="route('web.cart.cart')"
                                    class="w-full relative inline-flex items-center gap-2 px-6 py-3 border-2 border-red-rock hover:bg-red-rock hover:text-white text-red-rock"
                                >
                                    
                                    <div class="flex items-center gap-2 text-inherit">
                                        <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M3 3h2l1.5 12h11L19 6H6" />
                                            <circle cx="9" cy="19" r="1.2" />
                                            <circle cx="16" cy="19" r="1.2" />
                                        </svg>
                                        <span>Naročilo ({{ cartCount }})</span>
                                    </div>
                                </a>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
                <div class="md:flex hidden items-center gap-3">
                    <button
                        @click="router.visit(route('web.b2b'))"
                        class="text-nowrap relative inline-flex items-center gap-2 rounded-[10px] px-6 py-4 text-sm font-semibold text-iron-grey border-2 border-iron-grey hover:bg-iron-grey hover:text-white"
                    >
                       
                        Za podjetja
                    </button>
                    <LoginModal :errors="errors" :flash="flash" />
                    <a
                        :href="route('web.cart.cart')"
                        class="relative inline-flex items-center gap-2 rounded-[10px] px-6 py-3 border-2 border-red-rock hover:bg-red-rock hover:text-white text-red-rock"
                    >
                        
                        <div class="flex items-center gap-2 text-inherit">
                            <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M3 3h2l1.5 12h11L19 6H6" />
                                <circle cx="9" cy="19" r="1.2" />
                                <circle cx="16" cy="19" r="1.2" />
                            </svg>
                            <span class="hidden sm:inline">Naročilo</span>
                        </div>
                        <span class="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-white text-xs font-bold text-iron-grey shadow">{{ cartCount }}</span>
                    </a>
                </div>
            </div>
        </header>
        <div class="border-b border-iron-grey-light/30"></div>

        <v-main class="bg-stone-50">
            <div v-if="isUserPage && user" class="flex min-h-screen">
                <!-- Mobile backdrop overlay -->
                <div
                    v-if="sidebarOpen"
                    @click="store.closeSidebar()"
                    class="fixed inset-0 z-40 bg-black/50 md:hidden"
                    aria-hidden="true"
                ></div>

                <!-- Sidebar Navigation -->
                <aside
                    class="fixed inset-y-0 left-0 z-50 w-64 transform border-r border-iron-grey-light bg-white transition-transform duration-300 ease-in-out md:relative md:translate-x-0 md:z-auto flex flex-col"
                    :class="sidebarOpen ? 'translate-x-0' : '-translate-x-full'"
                >
                    <!-- Fixed Header Section -->
                    <div class="flex-shrink-0 p-6 pb-4">
                        <!-- Close button for mobile -->
                        <div class="mb-4 flex justify-end md:hidden">
                            <button
                                @click="store.closeSidebar()"
                                class="p-2 text-iron-grey/70 hover:text-iron-grey transition-colors"
                                aria-label="Close menu"
                            >
                                <svg class="h-6 w-6" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <!-- User Image Placeholder -->
                        <div class="mb-6 flex justify-center">
                            <div class="h-20 w-20 overflow-hidden rounded-full bg-linen flex items-center justify-center">
                                <svg class="h-12 w-12 text-iron-grey-light" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                                </svg>
                            </div>
                        </div>

                        <!-- User Greeting -->
                        <div class="mb-6 text-center">
                            <p class="text-sm font-semibold text-iron-grey">
                                Pozdravljeni, <span class="text-iron-grey">{{ userName }}</span>
                            </p>
                        </div>
                    </div>

                    <!-- Scrollable Navigation Menu -->
                    <nav class="flex-1 overflow-y-auto px-6 space-y-2">
                        <a
                            :href="route('web.user.dashboard')"
                            @click="store.closeSidebar()"
                            class="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors"
                            :class="isDashboardActive 
                                ? 'bg-theme-green-light text-iron-grey' 
                                : 'text-iron-grey/70 hover:bg-linen hover:text-iron-grey'"
                        >
                            <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                            </svg>
                            <span>Pregled</span>
                        </a>
                        <a
                            :href="route('web.user.update-profile')"
                            @click="store.closeSidebar()"
                            class="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors"
                            :class="isProfileActive 
                                ? 'bg-theme-green-light text-iron-grey' 
                                : 'text-iron-grey/70 hover:bg-linen hover:text-iron-grey'"
                        >
                            <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span>Profil</span>
                        </a>
                        <a
                            :href="route('web.user.orders.index')"
                            @click="store.closeSidebar()"
                            class="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors"
                            :class="isOrdersActive 
                                ? 'bg-theme-green-light text-iron-grey' 
                                : 'text-iron-grey/70 hover:bg-linen hover:text-iron-grey'"
                        >
                            <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>Naročila</span>
                        </a>
                        <a
                            :href="route('web.products.list')"
                            @click="store.closeSidebar()"
                            class="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors text-iron-grey/70 hover:bg-linen hover:text-iron-grey"
                        >
                            <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            <span>Ponudba</span>
                        </a>
                    </nav>

                    <!-- Fixed Logout Button at Bottom -->
                    <div class="flex-shrink-0 px-6 pt-4 pb-6 border-t border-iron-grey-light">
                        <button
                            @click="logout(); store.closeSidebar()"
                            class="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
                        >
                            <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                            </svg>
                            <span>Odjava</span>
                        </button>
                    </div>
                </aside>

                <!-- Main Content -->
                <div class="flex-1 p-4 md:p-6">
                    <slot />
                </div>
            </div>

            <!-- Regular content for non-user pages -->
            <div v-else>
                <slot />
            </div>
        </v-main>

        <footer class="bg-white border-t border-iron-grey-light/30">
            <div class="mx-auto max-w-6xl px-4 py-12">
                <div class="grid gap-10 md:grid-cols-4">
                    <div class="space-y-3 md:col-span-1">
                        <p class="text-xl font-semibold text-iron-grey">Kulfood</p>
                        <p class="text-sm text-iron-grey leading-relaxed old-standard-tt-regular uppercase">
                            Enostavno.<br>
                            Lahko.<br>
                            Okusno.<br>
                        </p>
                    </div>

                    <div class="space-y-3">
                        <p class="text-xs font-semibold uppercase tracking-wide text-iron-grey-dark">Navigacija</p>
                        <div class="flex flex-col gap-2 text-sm text-iron-grey/70">
                            <a :href="route('web.home')" class="hover:text-iron-grey">Domov</a>
                            <a :href="route('web.about-us')" class="hover:text-iron-grey">O nas</a>
                            <a :href="route('web.home') + '#categories'" class="hover:text-iron-grey">Kategorije</a>
                            <a :href="route('web.home') + '#how'" class="hover:text-iron-grey">Kako deluje</a>
                            <a :href="route('web.b2b')" class="hover:text-iron-grey">Za podjetja</a>
                        </div>
                    </div>

                    <div class="space-y-3">
                        <p class="text-xs font-semibold uppercase tracking-wide text-iron-grey-dark">Pravne informacije</p>
                        <div class="flex flex-col gap-2 text-sm text-iron-grey/70">
                            <a :href="route('web.privacy-policy')" class="hover:text-iron-grey">Politika zasebnosti</a>
                            <a :href="route('web.terms-conditions')" class="hover:text-iron-grey">Pogoji uporabe</a>
                            <a :href="route('web.cookie-policy')" class="hover:text-iron-grey">Politika piškotkov</a>
                            <a :href="route('web.impressum')" class="hover:text-iron-grey">Impresum</a>
                        </div>
                    </div>

                    <div class="space-y-3">
                        <p class="text-xs font-semibold uppercase tracking-wide text-iron-grey-dark">Kontakt</p>
                        <div class="space-y-1 text-sm text-iron-grey/70">
                            <p class="font-semibold text-iron-grey">Kulfood</p>
                            <p>Ajdovščina 4, 1000 Ljubljana</p>
                            <p>Davčna številka: SI 75339358</p>
                            <p>Matična številka: 7397950000</p>
                            <p>
                                Telefon:
                                <a href="tel:+38640723726" class="hover:text-iron-grey">+386 40 723 726</a>
                            </p>
                            <p>
                                E-naslov:
                                <a href="mailto:info@kulfood.si" class="hover:text-iron-grey">info@kulfood.si</a>
                            </p>
                        </div>
                    </div>
                </div>

                <div class="mt-10 flex flex-col gap-4 border-t border-iron-grey-light/30 pt-6 text-sm text-iron-grey/60 md:flex-row md:items-center md:justify-between">
                    <p>© {{ new Date().getFullYear() }} Kulfood. Vse pravice pridržane.</p>
                    <p class="text-iron-grey/50">Hvala, ker podpirate lokalne ponudnike.</p>
                </div>
            </div>
        </footer>

        <CookieBanner />
    </v-app>
</template>
<style scoped>
.v-list {
    padding: 0 !important;
}
.v-list-item {
    padding: 0 !important;
}
</style>